import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_narcan-dispenser-pro/artifacts/avzlwoog_IMG_9826.JPG";

const publicSafetyItems = [
    { to: "/public-safety/overdose-response", label: "Overdose Response", desc: "Rapid access to OTC Naloxone." },
    { to: "/public-safety/drink-spike-detection", label: "Drink Spike Detection", desc: "Detection kits made visible & accessible." },
];

const alertAutomationItems = [
    { to: "/alert-automation#ln2-scale", label: "LN₂ Scale + Workflow", desc: "Alert buttons + workflow automation for LN₂ tanks." },
    { to: "/alert-automation#equipment-alerts", label: "Equipment Alerts", desc: "One press creates a work order." },
    { to: "/alert-automation#automated-weight-alerts", label: "Automated Weight Alerts", desc: "Continuous scale monitoring." },
    { to: "/alert-automation#custom-alert-systems", label: "Custom Alert Systems", desc: "Sensors, notifications, admin portal." },
];

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [dropdown, setDropdown] = useState(null); // "public" | "alert" | null
    const [mobilePanel, setMobilePanel] = useState(null); // same enum for mobile expansion
    const navigate = useNavigate();
    const location = useLocation();
    const closeTimer = useRef(null);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Close dropdown on route change
    useEffect(() => {
        setDropdown(null);
        setOpen(false);
        setMobilePanel(null);
    }, [location.pathname, location.hash]);

    const openDropdown = (key) => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        setDropdown(key);
    };
    const scheduleClose = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setDropdown(null), 120);
    };

    const goHome = () => navigate("/");

    return (
        <header
            data-testid="navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-white/85 backdrop-blur-xl border-b border-slate-200"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
                <button
                    data-testid="navbar-logo"
                    onClick={goHome}
                    className="flex items-center gap-3 group"
                    aria-label="Kairos ORD home"
                >
                    <img
                        src={LOGO_URL}
                        alt="Kairos ORD"
                        className="h-10 w-10 object-contain rounded-sm bg-white"
                    />
                    <div className="hidden sm:flex flex-col leading-none">
                        <span className="font-display font-extrabold tracking-tight text-[#0A1428] text-lg">
                            KAIROS<span className="text-[#F0493E]">+</span>
                        </span>
                        <span className="font-body text-[10px] uppercase tracking-[0.25em] text-slate-500 mt-0.5">
                            ORD LLC
                        </span>
                    </div>
                </button>

                <nav className="hidden md:flex items-center gap-7">
                    <Link
                        to="/"
                        data-testid="nav-link-home"
                        className="link-underline text-sm font-semibold text-slate-700 hover:text-[#0A1428]"
                    >
                        Home
                    </Link>

                    <Link
                        to="/products"
                        data-testid="nav-link-products"
                        className="link-underline text-sm font-semibold text-slate-700 hover:text-[#0A1428]"
                    >
                        Products
                    </Link>

                    <DropdownTrigger
                        label="Public Safety"
                        testid="nav-link-public-safety"
                        active={dropdown === "public"}
                        onEnter={() => openDropdown("public")}
                        onLeave={scheduleClose}
                    />

                    <DropdownTrigger
                        label="Alert & Automation"
                        testid="nav-link-alert-automation"
                        active={dropdown === "alert"}
                        onEnter={() => openDropdown("alert")}
                        onLeave={scheduleClose}
                    />

                    <Link
                        to="/auto-shops"
                        data-testid="nav-link-auto-shops"
                        className="link-underline text-sm font-semibold text-slate-700 hover:text-[#0A1428]"
                    >
                        Auto Shops
                    </Link>

                    <Link
                        to="/about"
                        data-testid="nav-link-about"
                        className="link-underline text-sm font-semibold text-slate-700 hover:text-[#0A1428]"
                    >
                        About
                    </Link>
                </nav>

                <div className="flex items-center gap-3">
                    <Link
                        to="/contact"
                        data-testid="navbar-cta-demo"
                        className="btn-primary hidden sm:inline-flex items-center px-5 py-2.5 text-sm font-bold tracking-wide uppercase rounded-sm"
                    >
                        Contact
                    </Link>
                    <button
                        data-testid="navbar-mobile-toggle"
                        className="md:hidden p-2 text-[#0A1428]"
                        onClick={() => setOpen((v) => !v)}
                        aria-label="Toggle menu"
                    >
                        <svg
                            width="22"
                            height="22"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                        >
                            {open ? (
                                <path d="M6 6l12 12M18 6L6 18" />
                            ) : (
                                <path d="M4 7h16M4 12h16M4 17h16" />
                            )}
                        </svg>
                    </button>
                </div>
            </div>

            {/* Desktop dropdown panel */}
            {dropdown && (
                <div
                    data-testid={`nav-dropdown-${dropdown}`}
                    onMouseEnter={() => openDropdown(dropdown)}
                    onMouseLeave={scheduleClose}
                    className="hidden md:block absolute left-0 right-0 top-20 border-b border-slate-200 bg-white/95 backdrop-blur-xl shadow-[0_20px_50px_-25px_rgba(10,20,40,0.35)]"
                >
                    <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
                        <div className="text-[10px] font-bold uppercase tracking-[0.32em] text-[#F0493E] mb-6">
                            {dropdown === "public" ? "Public Safety" : "Alert & Automation"}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
                            {(dropdown === "public"
                                ? publicSafetyItems
                                : alertAutomationItems
                            ).map((item) => (
                                <Link
                                    key={item.to + item.label}
                                    to={item.to}
                                    data-testid={`nav-dropdown-item-${item.label
                                        .toLowerCase()
                                        .replace(/[^a-z0-9]+/g, "-")}`}
                                    className="group bg-white p-6 md:p-7 hover:bg-slate-50 transition-colors flex flex-col"
                                >
                                    <div className="font-display font-bold text-lg md:text-xl text-[#0A1428] tracking-tight group-hover:text-[#F0493E] transition-colors">
                                        {item.label}
                                    </div>
                                    <div className="mt-2 text-sm text-slate-600 font-medium leading-relaxed">
                                        {item.desc}
                                    </div>
                                    <div className="mt-4 h-px w-10 bg-[#0A1428] group-hover:bg-[#F0493E] transition-colors" />
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Mobile menu */}
            {open && (
                <div className="md:hidden bg-white border-t border-slate-200">
                    <div className="px-6 py-6 flex flex-col gap-1">
                        <Link
                            to="/"
                            data-testid="mobile-nav-home"
                            onClick={() => setOpen(false)}
                            className="py-3 text-base font-semibold text-slate-800 border-b border-slate-100"
                        >
                            Home
                        </Link>

                        <Link
                            to="/products"
                            data-testid="mobile-nav-products"
                            onClick={() => setOpen(false)}
                            className="py-3 text-base font-semibold text-slate-800 border-b border-slate-100"
                        >
                            Products
                        </Link>

                        <MobileDropdown
                            label="Public Safety"
                            testid="mobile-nav-public-safety"
                            items={publicSafetyItems}
                            expanded={mobilePanel === "public"}
                            onToggle={() =>
                                setMobilePanel((p) => (p === "public" ? null : "public"))
                            }
                            onItemClick={() => setOpen(false)}
                        />
                        <MobileDropdown
                            label="Alert & Automation"
                            testid="mobile-nav-alert-automation"
                            items={alertAutomationItems}
                            expanded={mobilePanel === "alert"}
                            onToggle={() =>
                                setMobilePanel((p) => (p === "alert" ? null : "alert"))
                            }
                            onItemClick={() => setOpen(false)}
                        />

                        <Link
                            to="/auto-shops"
                            data-testid="mobile-nav-auto-shops"
                            onClick={() => setOpen(false)}
                            className="py-3 text-base font-semibold text-slate-800 border-b border-slate-100"
                        >
                            Auto Shops
                        </Link>

                        <Link
                            to="/about"
                            data-testid="mobile-nav-about"
                            onClick={() => setOpen(false)}
                            className="py-3 text-base font-semibold text-slate-800 border-b border-slate-100"
                        >
                            About
                        </Link>
                        <Link
                            to="/contact"
                            data-testid="mobile-nav-cta"
                            onClick={() => setOpen(false)}
                            className="btn-primary mt-4 inline-flex items-center justify-center px-5 py-3 text-sm font-bold tracking-wide uppercase rounded-sm"
                        >
                            Contact
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
};

const DropdownTrigger = ({ label, testid, active, onEnter, onLeave }) => (
    <div
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        className="relative"
    >
        <button
            data-testid={testid}
            className={`link-underline text-sm font-semibold hover:text-[#0A1428] transition-colors flex items-center gap-1.5 ${
                active ? "text-[#0A1428]" : "text-slate-700"
            }`}
        >
            {label}
            <ChevronDown
                size={14}
                strokeWidth={2}
                className={`transition-transform ${active ? "rotate-180" : ""}`}
            />
        </button>
    </div>
);

const MobileDropdown = ({ label, testid, items, expanded, onToggle, onItemClick }) => (
    <div className="border-b border-slate-100">
        <button
            data-testid={testid}
            onClick={onToggle}
            className="w-full flex items-center justify-between py-3 text-base font-semibold text-slate-800"
        >
            <span>{label}</span>
            <ChevronDown
                size={16}
                strokeWidth={2}
                className={`transition-transform ${expanded ? "rotate-180" : ""}`}
            />
        </button>
        {expanded && (
            <div className="pb-4 pl-4 flex flex-col gap-3">
                {items.map((item) => (
                    <Link
                        key={item.to + item.label}
                        to={item.to}
                        onClick={onItemClick}
                        data-testid={`mobile-nav-item-${item.label
                            .toLowerCase()
                            .replace(/[^a-z0-9]+/g, "-")}`}
                        className="text-[15px] font-semibold text-slate-700 hover:text-[#F0493E]"
                    >
                        {item.label}
                    </Link>
                ))}
            </div>
        )}
    </div>
);
